import { css } from 'aphrodite';
import * as React from 'react';
import { useState } from 'react';
import { CoffeeLeadIssueCategory, IRange, IUser } from '../../../../../extViewmodels';
import { LoadingSpinner } from '../../../../../web/components/LoadingSpinner';
import { ExportIcon } from '../../../../../web/components/svgs/icons/ExportIcon';
import { ChevronDown } from '../../../../../web/components/svgs/icons/ChevronDown';
import { XIconNew } from '../../../../../web/components/svgs/icons/XIcon';
import { Button } from '../../../../../web/components-v2/Button';
import { ButtonGroup } from '../../../../../web/components-v2/ButtonGroup';
import { Calendar } from '../../../../../web/components-v2/Calendar';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../../../../web/components-v2/Select';
import { Popover, PopoverContent, PopoverTrigger } from '../../../../../web/components-v2/Popover';
import { UserCombobox, UserComboboxAvatarItem, UserComboboxStatus } from '../../../../../web/components/UserCombobox';
import { ComboboxContent, ComboboxInput, ComboboxList } from '../../../../../web/components-v2/Combobox';
import { TransparentButton } from '../../../../components/TransparentButton';
import { Fill } from '../../../../components/generics/Fill';
import { Group } from '../../../../components/generics/Group';
import { bs } from '../../../../../web/styles/styles';
import { CompletionStatus, IIssuesLogFilter } from '../index';
import { styleSheet } from '../styles';

const StatusOptions = [
	{ label: 'All', value: '' },
	{ label: 'Not Completed', value: 'incomplete' },
	{ label: 'Completed', value: 'complete' },
];

const CategoryOptions = [
	{ label: 'All Categories', value: '' },
	{ label: 'Wrong Location/Timezone', value: CoffeeLeadIssueCategory.WrongLocationOrTimeZone },
	{ label: 'Duplicate', value: CoffeeLeadIssueCategory.Duplicate },
	{ label: 'Current Client Needs Attention', value: CoffeeLeadIssueCategory.CurrentClientNeedsAttention },
	{ label: 'Other', value: CoffeeLeadIssueCategory.Other },
];

interface IDateRange {
	from: Date | null;
	to?: Date | null;
}

interface IProps {
	exportingCsv?: boolean;
	onChange?: (filter: IIssuesLogFilter) => void;
	onExport?: () => void;
}

export const ListHeader: React.FC<IProps> = ({ exportingCsv, onChange, onExport }) => {
	const [currentFilter, setCurrentFilter] = useState<IIssuesLogFilter>({});
	const [dateRange, setDateRange] = useState<IDateRange>({ from: null, to: null });

	const handleUserChange = (user: IUser | null) => {
		const next = { ...currentFilter, user: user || undefined };
		setCurrentFilter(next);
		onChange?.(next);
	};

	const handleDateRangeChange = (range: IDateRange) => {
		setDateRange(range);
		const date: IRange<Date> | undefined = range?.from && range?.to ? { start: range.from, end: range.to } : undefined;
		const next = { ...currentFilter, date };
		setCurrentFilter(next);
		onChange?.(next);
	};

	const clearDateRange = () => {
		setDateRange({ from: null, to: null });
		const next: IIssuesLogFilter = { ...currentFilter, date: undefined };
		setCurrentFilter(next);
		onChange?.(next);
	};

	const handleStatusChange = (value: string) => {
		const completionStatus = (value || undefined) as CompletionStatus;
		const next = { ...currentFilter, completionStatus };
		setCurrentFilter(next);
		onChange?.(next);
	};

	const handleCategoryChange = (value: string) => {
		const category = value || undefined;
		const next = { ...currentFilter, category: category as CoffeeLeadIssueCategory };
		setCurrentFilter(next);
		onChange?.(next);
	};

	return (
		<div className={css(styleSheet.filter)}>
			<div className={css(styleSheet.filterGroup)}>
				<label className={css(styleSheet.filterLabel)}>Reporter</label>
				<UserCombobox id={currentFilter.user?.id} onValueChange={handleUserChange}>
					<ComboboxInput placeholder='Filter by reporter' id='issues-log-user-combobox' showClear />
					<ComboboxContent>
						<UserComboboxStatus />
						<ComboboxList>
							{(user: IUser) => <UserComboboxAvatarItem key={`reporter-item-${user.id}`} user={user} />}
						</ComboboxList>
					</ComboboxContent>
				</UserCombobox>
			</div>

			<div className={css(styleSheet.filterGroup)}>
				<label className={css(styleSheet.filterLabel)}>Date Range</label>
				<Popover>
					<ButtonGroup styles={[styleSheet.dateButtonGroup]}>
						<PopoverTrigger asChild>
							<Button
								variant='outline'
								id='issues-log-dates'
								styles={[styleSheet.dateButton, dateRange.from || dateRange.to ? styleSheet.dateButtonActive : null]}
							>
								{dateRange?.from && dateRange?.to
									? `${dateRange.from.toLocaleDateString()} - ${dateRange.to.toLocaleDateString()}`
									: 'Filter by date'}
								{!dateRange?.from && !dateRange?.to ? <ChevronDown /> : null}
							</Button>
						</PopoverTrigger>
						{dateRange.from || dateRange.to ? (
							<Button variant='outline' onClick={clearDateRange}>
								<XIconNew />
							</Button>
						) : null}
					</ButtonGroup>
					<PopoverContent styles={[bs.wAuto, bs.overflowHidden, bs.p0]} align='start'>
						<Calendar
							mode='range'
							selected={dateRange}
							numberOfMonths={2}
							timeZone='UTC'
							onSelect={r => handleDateRangeChange(r)}
						/>
					</PopoverContent>
				</Popover>
			</div>

			<div className={css(styleSheet.filterGroup)}>
				<label className={css(styleSheet.filterLabel)}>Category</label>
				<Select value={currentFilter.category || ''} onValueChange={handleCategoryChange} items={CategoryOptions}>
					<SelectTrigger id='issue-category-select'>
						<SelectValue placeholder='All Categories' />
					</SelectTrigger>
					<SelectContent>
						{CategoryOptions.map(option => (
							<SelectItem key={option.value} value={option.value}>
								{option.label}
							</SelectItem>
						))}
					</SelectContent>
				</Select>
			</div>

			<div className={css(styleSheet.filterGroup)}>
				<label className={css(styleSheet.filterLabel)}>Status</label>
				<Select value={currentFilter.completionStatus || ''} onValueChange={handleStatusChange} items={StatusOptions}>
					<SelectTrigger id='issue-status-select'>
						<SelectValue placeholder='All' />
					</SelectTrigger>
					<SelectContent>
						{StatusOptions.map(option => (
							<SelectItem key={option.value} value={option.value}>
								{option.label}
							</SelectItem>
						))}
					</SelectContent>
				</Select>
			</div>

			<Fill />
			<TransparentButton onClick={onExport} disabled={exportingCsv}>
				<Group fullWidth={false}>
					{exportingCsv ? <LoadingSpinner type='extra-small' /> : <ExportIcon />}
					<span className={css(styleSheet.regularText)}>Export</span>
				</Group>
			</TransparentButton>
		</div>
	);
};
